import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import axios from "axios";

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

import { BarrioContext } from "../components/BarrioContext";
import { useAuth } from "../components/AuthContext";
import { useTerminologia } from "../hooks/useTerminologia";

const TIPO_PAGO_COLORES = {
  Parcial:  { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
  Total:    { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
  Anticipo: { bg: '#eff8ff', color: '#175cd3', border: '#b2ddff' },
};

const initialFormData = {
  pagoProveedorId: 0,
  proveedorId: null,
  grupoGastoId: null,
  tipoPagoProveedorId: null,
  monto: null,
  fechaPago: new Date(),
  nroFactura: "",
  detalle: "",
  archivo: null,
};

const PagoProveedores = () => {
  const toast = useRef(null);
  const { barrio } = useContext(BarrioContext);
  const { administracionId } = useAuth();
  const t = useTerminologia();

  const HOST_API = process.env.REACT_APP_URL;

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const urlComprobante = (url) => `${HOST_API}${url}`.replace(/([^:])\/\/+/g, '$1/');

  // ---- combos ----
  const [gruposGasto, setGruposGasto] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresTodos, setProveedoresTodos] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);

  useEffect(() => {
    axios.get(`${HOST_API}api/grupogastos/GetGrupoGastos?administracionId=${administracionId}`)
      .then((res) => setGruposGasto(res.data))
      .catch((err) => console.error("Error al obtener grupos de gasto:", err));

    axios.get(`${HOST_API}api/pagoProveedor/getTiposPago`)
      .then((res) => setTiposPago(res.data))
      .catch((err) => console.error("Error al obtener tipos de pago:", err));

    axios.get(`${HOST_API}api/proveedores/GetProveedoresCombo?barrioId=${barrio.id}&administracionId=${administracionId}&grupoGastoId=0`)
      .then((res) => setProveedoresTodos(res.data.map((p) => ({ ...p, proveedorId: Number(p.proveedorId) }))))
      .catch((err) => console.error("Error al obtener proveedores:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [administracionId, barrio.id]);

  const handleGrupoGastoChange = (grupoGastoId) => {
    setFormData((prev) => ({ ...prev, grupoGastoId, proveedorId: null }));
    axios.get(`${HOST_API}api/proveedores/GetProveedoresCombo?barrioId=${barrio.id}&administracionId=${administracionId}&grupoGastoId=${grupoGastoId}`)
      .then((res) => setProveedores(res.data.map((p) => ({ ...p, proveedorId: Number(p.proveedorId) }))))
      .catch((err) => console.error("Error al obtener proveedores:", err));
  };

  // ---- Nuevo Pago / revisar y confirmar ----
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [guardando, setGuardando] = useState(false);

  const handleChange = (campo, valor) => setFormData((prev) => ({ ...prev, [campo]: valor }));

  const handleNuevoPago = () => {
    setFormData(initialFormData);
    setProveedores([]);
    setFlashHint(null);
    setFlashDuplicado(null);
    setVisible(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (guardando) return;

    if (flashQueue.length > 0 && flashDuplicado) {
      toast.current.show({ severity: "warn", summary: "Proveedores Flash", detail: "Este comprobante parece estar duplicado — saltalo o revisalo antes de guardar.", life: 3000 });
      return;
    }

    if (!formData.proveedorId || !formData.tipoPagoProveedorId || !formData.monto || !formData.fechaPago) {
      toast.current.show({ severity: "warn", summary: "Pago a Proveedores", detail: "Completá proveedor, tipo de pago, monto y fecha.", life: 3000 });
      return;
    }

    setGuardando(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const payload = {
        pagoProveedorId: formData.pagoProveedorId || 0,
        administracionId,
        barrioId: barrio.id,
        proveedorId: formData.proveedorId,
        grupoGastoId: formData.grupoGastoId || null,
        tipoPagoProveedorId: formData.tipoPagoProveedorId,
        monto: parseFloat(formData.monto) || 0,
        fechaPago: new Date(formData.fechaPago).toISOString().split("T")[0] + "T00:00:00",
        nroFactura: formData.nroFactura || "",
        detalle: formData.detalle || "",
        usuarioId: user?.id ?? user?.usuarioId ?? null,
      };

      const fd = new FormData();
      fd.append("model", JSON.stringify(payload));
      if (formData.archivo) fd.append("file", formData.archivo);

      await axios.post(`${HOST_API}api/pagoProveedor/savePago`, fd);

      toast.current.show({ severity: "success", summary: "Pago a Proveedores", detail: "Pago guardado correctamente", life: 2000 });

      if (flashQueue.length > 0) {
        avanzarFlash();
      } else {
        setVisible(false);
        setFormData(initialFormData);
        setProveedores([]);
      }
      fetchPagos();
      fetchFacturasPendientes();
    } catch (err) {
      console.error("❌ Error al guardar pago:", err);
      toast.current.show({ severity: "error", summary: "Pago a Proveedores - Error", detail: "Error al guardar el pago", life: 2500 });
    } finally {
      setGuardando(false);
    }
  };

  // ---- Proveedores Flash ----
  const [flashVisible, setFlashVisible] = useState(false);
  const [flashProcessing, setFlashProcessing] = useState(false);
  const [flashProgressText, setFlashProgressText] = useState("");
  const [flashQueue, setFlashQueue] = useState([]);
  const [flashIndex, setFlashIndex] = useState(0);
  const [flashHint, setFlashHint] = useState(null);
  const [flashDuplicado, setFlashDuplicado] = useState(null);

  const cargarComprobanteFlashEnForm = (item) => {
    setFormData({
      ...initialFormData,
      proveedorId: item.proveedorId,
      grupoGastoId: item.grupoGastoId,
      monto: item.monto != null ? item.monto : null,
      fechaPago: item.fecha || new Date(),
      nroFactura: item.nroFactura || "",
      detalle: item.detalle || "",
      archivo: item.file,
    });

    setFlashHint(item.encontrado ? null : { cuit: item.cuit });
    setFlashDuplicado(item.duplicado ? item.duplicadoInfo || {} : null);

    if (item.grupoGastoId) {
      axios.get(`${HOST_API}api/proveedores/GetProveedoresCombo?barrioId=${barrio.id}&administracionId=${administracionId}&grupoGastoId=${item.grupoGastoId}`)
        .then((res) => setProveedores(res.data.map((p) => ({ ...p, proveedorId: Number(p.proveedorId) }))))
        .catch(() => {});
    } else {
      setProveedores([]);
    }

    setVisible(true);
  };

  const avanzarFlash = () => {
    const nextIndex = flashIndex + 1;
    if (nextIndex < flashQueue.length) {
      setFlashIndex(nextIndex);
      cargarComprobanteFlashEnForm(flashQueue[nextIndex]);
    } else {
      setFlashQueue([]);
      setFlashIndex(0);
      setFlashHint(null);
      setFlashDuplicado(null);
      setVisible(false);
      setFormData(initialFormData);
      setProveedores([]);
      toast.current.show({ severity: "success", summary: "Proveedores Flash", detail: "Se cargaron todos los comprobantes", life: 2000 });
    }
  };

  const handleSaltarFlash = () => avanzarFlash();

  const handleProcesarComprobantes = async (event) => {
    const files = event.files || [];
    if (files.length === 0) return;

    setFlashProcessing(true);

    const queue = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFlashProgressText(`Analizando comprobante ${i + 1} de ${files.length}…`);

      try {
        const fd = new FormData();
        fd.append("file", file);

        const res = await axios.post(
          `${HOST_API}api/pagoProveedor/analizarComprobante?administracionId=${administracionId}&barrioId=${barrio.id}`,
          fd
        );
        const data = res.data;

        queue.push({
          file,
          cuit: data.cuit,
          monto: data.monto,
          fecha: data.fecha ? new Date(data.fecha) : null,
          nroFactura: data.nroFactura || null,
          detalle: data.detalle || null,
          proveedorId: data.proveedorId || null,
          nombreProveedor: data.nombreProveedor || null,
          grupoGastoId: data.grupoGastoId || null,
          encontrado: !!data.encontrado,
          duplicado: !!data.duplicado,
          duplicadoInfo: data.duplicadoInfo || null,
        });
      } catch (err) {
        console.error("❌ Error al analizar comprobante:", file.name, err);
        toast.current.show({ severity: "warn", summary: "Proveedores Flash", detail: `No se pudo analizar "${file.name}"`, life: 2500 });
      }
    }

    // Chequeo de duplicado dentro del mismo lote (todavia no esta guardado en la base).
    queue.forEach((item, idx) => {
      if (item.duplicado || !item.cuit) return;
      const repetidoEnLote = queue.slice(0, idx).some((otro) =>
        otro.cuit === item.cuit && (
          (item.nroFactura && otro.nroFactura && item.nroFactura.trim().toLowerCase() === otro.nroFactura.trim().toLowerCase())
          || (item.monto != null && otro.monto != null && Math.abs(Number(item.monto) - Number(otro.monto)) < 0.01)
        )
      );
      if (repetidoEnLote) {
        item.duplicado = true;
        item.duplicadoInfo = { enEsteLote: true };
      }
    });

    setFlashProcessing(false);
    setFlashProgressText("");
    setFlashVisible(false);
    event.options.clear();

    if (queue.length === 0) {
      toast.current.show({ severity: "error", summary: "Proveedores Flash", detail: "No se pudo analizar ningún comprobante", life: 2500 });
      return;
    }

    setFlashQueue(queue);
    setFlashIndex(0);
    cargarComprobanteFlashEnForm(queue[0]);
  };

  // ---- Facturas Pendientes de Pago (solo lectura, no se ata al pago) ----
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);

  const fetchFacturasPendientes = useCallback(async () => {
    if (!barrio?.id) return;
    setLoadingPendientes(true);
    try {
      const res = await axios.get(`${HOST_API}api/pagoProveedor/getFacturasPendientes?administracionId=${administracionId}&barrioId=${barrio.id}`);
      setFacturasPendientes(res.data || []);
    } catch (err) {
      console.error("Error al obtener facturas pendientes:", err);
    } finally {
      setLoadingPendientes(false);
    }
  }, [HOST_API, administracionId, barrio?.id]);

  useEffect(() => { fetchFacturasPendientes(); }, [fetchFacturasPendientes]);

  // ---- Consulta de Comprobantes ----
  const [pagos, setPagos] = useState([]);
  const [loadingPagos, setLoadingPagos] = useState(false);
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(null);
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(null);
  const [filtroProveedorId, setFiltroProveedorId] = useState(null);
  const [filtroTipoPagoId, setFiltroTipoPagoId] = useState(null);
  const [filtroNroFactura, setFiltroNroFactura] = useState("");

  const fetchPagos = useCallback(async () => {
    if (!barrio?.id) return;
    setLoadingPagos(true);
    try {
      const params = new URLSearchParams();
      params.append("administracionId", administracionId);
      params.append("barrioId", barrio.id);
      if (filtroFechaDesde) params.append("fechaDesde", formatDate(filtroFechaDesde));
      if (filtroFechaHasta) params.append("fechaHasta", formatDate(filtroFechaHasta));
      if (filtroProveedorId) params.append("proveedorId", filtroProveedorId);
      if (filtroTipoPagoId) params.append("tipoPagoProveedorId", filtroTipoPagoId);
      if (filtroNroFactura) params.append("nroFactura", filtroNroFactura);

      const res = await axios.get(`${HOST_API}api/pagoProveedor/getPagos?${params.toString()}`);
      setPagos(res.data || []);
    } catch (err) {
      console.error("Error al obtener pagos:", err);
    } finally {
      setLoadingPagos(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [HOST_API, administracionId, barrio?.id]);

  useEffect(() => { fetchPagos(); }, [fetchPagos]);

  const handleEliminarPago = (row) => {
    confirmDialog({
      message: `¿Eliminar el pago a ${row.nombreProveedor} por $${Number(row.monto).toLocaleString("es-AR")}?`,
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        try {
          await axios.delete(`${HOST_API}api/pagoProveedor/deletePago/${row.pagoProveedorId}/${administracionId}`);
          toast.current.show({ severity: "success", summary: "Pago a Proveedores", detail: "Pago eliminado", life: 2000 });
          fetchPagos();
        } catch (err) {
          toast.current.show({ severity: "error", summary: "Error", detail: "No se pudo eliminar el pago", life: 2500 });
        }
      },
    });
  };

  const tipoPagoTemplate = (row) => {
    const desc = row.tipoPagoDescripcion || "";
    const clave = Object.keys(TIPO_PAGO_COLORES).find((k) => desc.includes(k));
    const s = TIPO_PAGO_COLORES[clave] || { bg: '#f2f4f7', color: '#344054', border: '#d0d5dd' };
    return (
      <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99 }}>
        {row.tipoPagoDescripcion}
      </span>
    );
  };

  if (!barrio?.id) {
    return (
      <div>
        <h4>Pago a Proveedores</h4>
        <p>Elegí un {t.barrio} en el selector de la barra superior para continuar.</p>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <Toast ref={toast} />

      <h4>Pago a Proveedores</h4>

      <div className="flex gap-2 flex-wrap mb-4">
        <Button label="Nuevo Pago" icon="pi pi-plus" onClick={handleNuevoPago} />
        <Button label="Proveedores Flash" icon="pi pi-bolt" className="p-button-outlined" onClick={() => setFlashVisible(true)} />
      </div>

      {/* ── Dialog: Proveedores Flash (selección de comprobantes) ── */}
      <Dialog
        header="Proveedores Flash — Cargar comprobantes"
        visible={flashVisible}
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "90vw" }}
        modal
        closable={!flashProcessing}
        onHide={() => !flashProcessing && setFlashVisible(false)}
      >
        <p>
          Subí uno o varios comprobantes de pago (PDF o imagen). Se van a analizar
          automáticamente y después los vas a poder revisar y confirmar uno por uno.
        </p>
        {flashProcessing ? (
          <div className="flex flex-column align-items-center gap-3 p-4">
            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
            <span>{flashProgressText}</span>
          </div>
        ) : (
          <FileUpload
            name="comprobantes"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            maxFileSize={5000000}
            customUpload
            uploadHandler={handleProcesarComprobantes}
            chooseLabel="Seleccionar comprobantes"
            uploadLabel="Procesar Comprobantes"
            cancelLabel="Cancelar"
            emptyTemplate={
              <div style={{ border: "2px dashed #cbd5e1", borderRadius: 10, padding: "2rem 1rem", textAlign: "center", color: "#667085" }}>
                <i className="pi pi-cloud-upload" style={{ fontSize: "2rem", color: "#98a2b3" }} />
                <p style={{ margin: "0.5rem 0 0" }}>Arrastrá y soltá acá tus comprobantes, o usá el botón "Seleccionar comprobantes"</p>
              </div>
            }
          />
        )}
      </Dialog>

      {/* ── Dialog: Nuevo Pago / revisar y confirmar ── */}
      <Dialog
        header={flashQueue.length > 0 ? `Comprobante Flash (${flashIndex + 1}/${flashQueue.length}) — revisar y confirmar` : "Nuevo Pago"}
        visible={visible}
        style={{ width: "50vw" }}
        breakpoints={{ "960px": "100vw" }}
        modal
        onHide={() => {
          setVisible(false);
          setFlashQueue([]);
          setFlashIndex(0);
          setFlashHint(null);
          setFlashDuplicado(null);
        }}
      >
        <form className="p-fluid grid" onSubmit={handleSubmit}>
          {flashDuplicado && (
            <div className="col-12" style={{ background: "#FDECEA", border: "1px solid #F5A9A0", borderRadius: 8, padding: "10px 14px", marginBottom: 8, color: "#8A1F11", fontSize: ".9rem" }}>
              <b>⚠ Este comprobante parece estar duplicado.</b>{" "}
              {flashDuplicado.enEsteLote
                ? "Coincide con otro comprobante de este mismo lote (mismo proveedor y monto o nro de factura)."
                : `Ya existe un pago cargado a este proveedor por $${flashDuplicado.monto != null ? Number(flashDuplicado.monto).toLocaleString("es-AR") : "?"}${flashDuplicado.nroFactura ? ` (factura ${flashDuplicado.nroFactura})` : ""}.`}{" "}
              No se puede guardar así — revisá los datos o saltá este comprobante.
            </div>
          )}

          <div className="col-12 flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <label>Grupo de Gasto</label>
              <Dropdown
                value={formData.grupoGastoId || null}
                options={gruposGasto}
                optionValue="grupoGastoId"
                optionLabel="descripcion"
                onChange={(e) => handleGrupoGastoChange(e.value)}
                placeholder="Seleccione"
                className="p-inputtext-sm w-full"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label>Proveedor *</label>
              <Dropdown
                value={formData.proveedorId || null}
                options={proveedores}
                optionValue="proveedorId"
                optionLabel="nombreProveedor"
                onChange={(e) => handleChange("proveedorId", e.value)}
                placeholder="Seleccione"
                className="p-inputtext-sm w-full"
              />
              {flashHint && (
                <small className="block mt-1" style={{ color: "#d97706" }}>
                  CUIT detectado: {flashHint.cuit || "no detectado"} (proveedor no encontrado, seleccioná uno o cargalo)
                </small>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <label>Tipo de Pago *</label>
              <Dropdown
                value={formData.tipoPagoProveedorId || null}
                options={tiposPago}
                optionValue="tipoPagoProveedorId"
                optionLabel="descripcion"
                onChange={(e) => handleChange("tipoPagoProveedorId", e.value)}
                placeholder="Seleccione"
                className="p-inputtext-sm w-full"
              />
            </div>
          </div>

          <div className="col-12 flex flex-wrap gap-3 mt-3">
            <div className="w-full md:w-4">
              <label>Monto *</label>
              <InputNumber
                value={formData.monto}
                onValueChange={(e) => handleChange("monto", e.value)}
                mode="currency" currency="ARS" locale="es-AR"
                className="p-inputtext-sm w-full"
              />
            </div>
            <div className="w-full md:w-4">
              <label>Fecha de Pago *</label>
              <Calendar
                value={formData.fechaPago}
                onChange={(e) => handleChange("fechaPago", e.value)}
                dateFormat="dd/mm/yy"
                showIcon
                className="p-inputtext-sm w-full"
              />
            </div>
            <div className="w-full md:w-4">
              <label>Nro de Factura/Comprobante</label>
              <InputText
                value={formData.nroFactura || ""}
                onChange={(e) => handleChange("nroFactura", e.target.value)}
                className="p-inputtext-sm w-full"
              />
            </div>
          </div>

          <div className="col-12 flex flex-wrap gap-3 mt-3">
            <div className="w-full md:w-8">
              <label>Detalle</label>
              <InputText
                value={formData.detalle || ""}
                onChange={(e) => handleChange("detalle", e.target.value)}
                className="p-inputtext-sm w-full"
              />
            </div>
            <div className="w-full md:w-4">
              <label>Comprobante</label>
              <FileUpload
                mode="basic"
                name="archivo"
                accept=".pdf,.jpg,.jpeg,.png"
                maxFileSize={5000000}
                chooseLabel="Adjuntar comprobante"
                onSelect={(e) => handleChange("archivo", e.files[0])}
                onClear={() => handleChange("archivo", null)}
              />
            </div>
          </div>

          <div className="col-12 mt-3 flex gap-2">
            <Button
              label={guardando ? "Guardando…" : (flashQueue.length > 0 ? "Guardar y Siguiente" : "Guardar")}
              icon="pi pi-save"
              type="submit"
              disabled={guardando || (flashQueue.length > 0 && !!flashDuplicado)}
              loading={guardando}
            />
            {flashQueue.length > 0 && (
              <Button label="Saltar este comprobante" icon="pi pi-step-forward" type="button" className="p-button-text" disabled={guardando} onClick={handleSaltarFlash} />
            )}
          </div>
        </form>
      </Dialog>

      {/* ── Facturas Pendientes de Pago ── */}
      <h5 className="mt-4">Facturas Pendientes de Pago</h5>
      <DataTable value={facturasPendientes} loading={loadingPendientes} paginator rows={5} emptyMessage="No hay facturas pendientes de pago." size="small" className="mb-4">
        <Column field="fechaComprobante" header="Fecha" body={(r) => r.fechaComprobante ? new Date(r.fechaComprobante).toLocaleDateString("es-AR") : ""} />
        <Column field="nombreProveedor" header="Proveedor" />
        <Column field="grupoGasto" header="Grupo de Gasto" />
        <Column field="comprobante" header="Comprobante" />
        <Column field="totalGasto" header="Monto" body={(r) => `$${Number(r.totalGasto).toLocaleString("es-AR")}`} />
      </DataTable>

      {/* ── Consulta de Comprobantes ── */}
      <h5>Consulta de Comprobantes de Pago</h5>
      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div>
          <label className="block mb-1">Desde</label>
          <Calendar value={filtroFechaDesde} onChange={(e) => setFiltroFechaDesde(e.value)} showIcon showButtonBar dateFormat="dd/mm/yy" className="p-inputtext-sm" />
        </div>
        <div>
          <label className="block mb-1">Hasta</label>
          <Calendar value={filtroFechaHasta} onChange={(e) => setFiltroFechaHasta(e.value)} showIcon showButtonBar dateFormat="dd/mm/yy" className="p-inputtext-sm" />
        </div>
        <div style={{ minWidth: 200 }}>
          <label className="block mb-1">Proveedor</label>
          <Dropdown value={filtroProveedorId} options={proveedoresTodos} optionValue="proveedorId" optionLabel="nombreProveedor" onChange={(e) => setFiltroProveedorId(e.value)} placeholder="Todos" showClear filter className="p-inputtext-sm w-full" />
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="block mb-1">Tipo de Pago</label>
          <Dropdown value={filtroTipoPagoId} options={tiposPago} optionValue="tipoPagoProveedorId" optionLabel="descripcion" onChange={(e) => setFiltroTipoPagoId(e.value)} placeholder="Todos" showClear className="p-inputtext-sm w-full" />
        </div>
        <div>
          <label className="block mb-1">Nro Factura/Comprobante</label>
          <InputText value={filtroNroFactura} onChange={(e) => setFiltroNroFactura(e.target.value)} className="p-inputtext-sm" />
        </div>
        <Button label="Buscar" icon="pi pi-search" onClick={fetchPagos} />
      </div>

      <DataTable value={pagos} loading={loadingPagos} paginator rows={10} emptyMessage="No se encontraron comprobantes de pago." size="small">
        <Column field="fechaPago" header="Fecha" body={(r) => new Date(r.fechaPago).toLocaleDateString("es-AR")} sortable />
        <Column field="nombreProveedor" header="Proveedor" sortable />
        <Column field="tipoPagoDescripcion" header="Tipo de Pago" body={tipoPagoTemplate} />
        <Column field="monto" header="Monto" body={(r) => `$${Number(r.monto).toLocaleString("es-AR")}`} sortable />
        <Column field="nroFactura" header="Nro Factura" />
        <Column field="detalle" header="Detalle" />
        <Column
          header="Comprobante"
          body={(r) => r.comprobanteUrl
            ? <Button icon="pi pi-file" rounded text severity="info" tooltip="Ver comprobante" onClick={() => window.open(urlComprobante(r.comprobanteUrl), "_blank")} />
            : null}
        />
        <Column
          header="Acciones"
          body={(r) => (
            <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Eliminar" onClick={() => handleEliminarPago(r)} />
          )}
        />
      </DataTable>
    </>
  );
};

export default PagoProveedores;
